import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { FabricService } from 'src/app/services/fabric/fabric.service';

const SERVICE_NAME = 'mgmt-instance-resources';

@Injectable({
  providedIn: 'root'
})
export class DisasterRecoveryService {

  private drInProgressSubject = new BehaviorSubject<boolean>(false);
  drInProgress$ = this.drInProgressSubject.asObservable();

  private drInstanceSubject = new BehaviorSubject<any>(null);
  drInstance$ = this.drInstanceSubject.asObservable();

  private isDREnabledSubject = new BehaviorSubject<boolean>(false);
  isDREnabled$ = this.isDREnabledSubject.asObservable();

  private drValidatedSubject = new BehaviorSubject<boolean>(false);
  drValidated$ = this.drValidatedSubject.asObservable();

  private lastActiveDrInstanceId: string | null = null;
  private triggeredBlueprintId: string | null = null;
  private drHasSecondaryRegion: boolean = false;

  setDrValidated(validated: boolean): void {
    this.drValidatedSubject.next(validated);
  }

  resetDrValidated(): void {
    this.drValidatedSubject.next(false);
  }

  constructor(private fabricService: FabricService) {}

  setDrStatus(instances: any[], isComplete: boolean = false): void {
    const drLifecycleStates = [
      'failover_inprogress',
      'pre_fallback_inprogress',
      'fallback_inprogress',
      'post_fallback_inprogress'
    ];

    const drInstance = instances?.find(instance =>
      drLifecycleStates.includes(instance.lifecycleState?.toLowerCase())
    );
    const isDrInProgress = !!drInstance;

    if (isDrInProgress) {
      this.drInProgressSubject.next(true);
      this.drInstanceSubject.next(drInstance);
      this.lastActiveDrInstanceId = drInstance.id;
    } else if (isComplete) {
      this.drInProgressSubject.next(false);
      this.drInstanceSubject.next(null);
      this.lastActiveDrInstanceId = null;
      this.triggeredBlueprintId = null;
    } else if (this.lastActiveDrInstanceId) {
      const previousDrInstance = instances?.find(i => i.id === this.lastActiveDrInstanceId);
      if (previousDrInstance && !drLifecycleStates.includes(previousDrInstance.lifecycleState?.toLowerCase())) {
        this.drInProgressSubject.next(false);
        this.drInstanceSubject.next(null);
        this.lastActiveDrInstanceId = null;
        this.triggeredBlueprintId = null;
      }
    }
  }

  clearDrStatus(): void {
    this.drInProgressSubject.next(false);
    this.drInstanceSubject.next(null);
    this.lastActiveDrInstanceId = null;
    this.drValidatedSubject.next(false);
    this.triggeredBlueprintId = null;
    this.drHasSecondaryRegion = false;
  }

  updateDrInstance(instance: any): void {
    this.drInstanceSubject.next(instance);
    if (instance) {
      this.drInProgressSubject.next(true);
      this.lastActiveDrInstanceId = instance.id;
    } else {
      this.drInProgressSubject.next(false);
      this.lastActiveDrInstanceId = null;
    }
  }

  setDREnabled(enabled: boolean): void {
    this.isDREnabledSubject.next(enabled);
    this.drHasSecondaryRegion = enabled;
    if (!enabled) {
      this.clearDrStatus();
    }
  }

  setDrInProgress(blueprintId?: string): void {
    this.drInProgressSubject.next(true);
    this.drValidatedSubject.next(true);
    if (blueprintId) {
      this.triggeredBlueprintId = blueprintId;
    }
  }

  getTriggeredBlueprintId(): string | null {
    return this.triggeredBlueprintId;
  }

  clearTriggeredBlueprintId(): void {
    this.triggeredBlueprintId = null;
  }

  getDRInstances(instances: any[]): any[] {
    const drLifecycleStates = [
      'failover_inprogress',
      'pre_fallback_inprogress',
      'fallback_inprogress',
      'post_fallback_inprogress'
    ];
    const drInstances = instances?.filter(instance =>
      drLifecycleStates.includes(instance.lifecycleState?.toLowerCase())
    ) || [];

    return drInstances.sort((a: any, b: any) =>
      new Date(b.dateUpdated || b.updatedAt).getTime() - new Date(a.dateUpdated || a.updatedAt).getTime()
    );
  }

  isDRStateActive(lifecycleState: string | null | undefined): boolean {
    if (!lifecycleState) return false;
    const drLifecycleStates = [
      'failover_inprogress',
      'pre_fallback_inprogress',
      'fallback_inprogress',
      'post_fallback_inprogress'
    ];
    return drLifecycleStates.includes(lifecycleState.toLowerCase());
  }
}
